import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge, Sel, Spinner, Field } from "@/components/kit";
import { FeeLines, PayDialog, ReceiptDialog } from "@/components/Payment";
import { api, fmtErr, dt, dur, peso, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const OPEN = ["active", "payment_pending", "paid", "exit_pending", "exception"];

export default function SessionSheet({ id, onClose, onChanged }) {
  const { can } = useAuth();
  const [s, setS] = useState(null);
  const [pay, setPay] = useState(false);
  const [rcpt, setRcpt] = useState(null);
  const [disc, setDisc] = useState({ type: "", value: "", reference: "" });
  const load = useCallback(async () => {
    if (!id) return;
    try {
      setS((await api.get(`/sessions/${id}`)).data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, [id]);
  useEffect(() => {
    setS(null);
    load();
  }, [load]);
  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  const open = s && OPEN.includes(s.status);
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto bg-card sm:max-w-lg" data-testid="session-sheet">
        <SheetHeader><SheetTitle className="font-display">Parking Session</SheetTitle></SheetHeader>
        {!s ? <Spinner /> : (
          <div className="mt-4 space-y-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="plate text-xl" data-testid="session-plate">{s.plate}</div>
                <div className="mt-1 font-mono text-xs text-muted-foreground">{s.session_no} • Ticket {s.ticket_code}</div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <StatusBadge status={s.status} testid="session-status" />
                <StatusBadge status={s.payment_status} />
                {s.offline && <StatusBadge status="offline" tone="amber" />}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[["Branch", s.branch_code], ["Vehicle", label(s.vehicle_type)], ["Entry lane", s.entry_lane_name], ["Method", label(s.entry_method)],
                ["Entry", dt(s.entry_time)], ["Duration", dur(s.duration_min)], ["Zone / Slot", `${s.zone_name || "—"} / ${s.slot_code || "—"}`], ["Rate", s.rate_plan_name],
                ["Exit", dt(s.exit_time)], ["Exit lane", s.exit_lane_name || "—"]].map(([k, v]) => (
                <div key={k}><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div><div className="font-medium">{v || "—"}</div></div>
              ))}
            </div>
            {s.lpr_result && <div className="text-xs text-muted-foreground">LPR: {s.lpr_result.plate} ({Math.round(s.lpr_result.confidence * 100)}% conf.)</div>}
            <div className="rounded-lg border p-3"><FeeLines fee={s.fee} /></div>
            {open && (
              <div className="flex flex-wrap gap-2">
                {s.fee.balance > 0 && can("add") && <Button data-testid="session-pay-btn" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => setPay(true)}>Collect {peso(s.fee.balance)}</Button>}
                {can("edit") && !s.lost_ticket && <Button variant="outline" data-testid="session-lost-ticket-btn" onClick={() => act(() => api.post(`/sessions/${s.id}/lost-ticket`), "Lost ticket penalty applied")}>Lost ticket</Button>}
                {can("approve") && <Button variant="outline" className="text-red-500" data-testid="session-cancel-btn" onClick={() => { const r = window.prompt("Cancel reason"); r && act(() => api.post(`/sessions/${s.id}/cancel`, { reason: r }), "Session cancelled"); }}>Cancel</Button>}
              </div>
            )}
            {open && can("edit") && (
              <div className="rounded-lg border p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Apply discount / validation</div>
                <div className="grid grid-cols-2 gap-2">
                  <Sel value={disc.type} onChange={(v) => setDisc({ ...disc, type: v })} testid="discount-type" className="w-full"
                    options={[{ value: "pwd", label: "PWD (20%)" }, { value: "senior", label: "Senior (20%)" }, { value: "merchant_validation", label: "Merchant validation (₱)" }, { value: "promo", label: "Promo (%)" }]} />
                  <Input data-testid="discount-value" placeholder="Value" value={disc.value} onChange={(e) => setDisc({ ...disc, value: e.target.value })} />
                  <Input data-testid="discount-ref" className="col-span-2" placeholder="Reference (ID no. / merchant / promo code)" value={disc.reference} onChange={(e) => setDisc({ ...disc, reference: e.target.value })} />
                </div>
                <Button size="sm" className="mt-2" data-testid="discount-apply" disabled={!disc.type}
                  onClick={() => act(() => api.post(`/sessions/${s.id}/discount`, { ...disc, value: Number(disc.value || 0) }), "Discount applied")}>Apply</Button>
              </div>
            )}
            {!!s.payments?.length && (
              <Field label="Payments">
                {s.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b py-1.5 text-sm">
                    <span className="font-mono text-xs">{p.receipt_no || p.transaction_id || "pending"}</span>
                    <span>{label(p.method)}</span>
                    <span className="font-mono">{peso(p.amount)}</span>
                    <StatusBadge status={p.status} />
                    {p.receipt_no && <button className="text-xs text-primary underline" data-testid={`session-receipt-${p.receipt_no}`} onClick={async () => setRcpt((await api.get(`/payments/${p.id}/receipt`)).data)}>Receipt</button>}
                  </div>
                ))}
              </Field>
            )}
            <Field label="Timeline">
              {s.events.map((e) => (
                <div key={e.id} className="flex justify-between text-xs"><span>{label(e.type)} • {e.lane_name}</span><span className="font-mono text-muted-foreground">{dt(e.at)}</span></div>
              ))}
            </Field>
            <PayDialog session={s} fee={s.fee} open={pay} onOpenChange={setPay} onPaid={() => { load(); onChanged?.(); }} />
            <ReceiptDialog data={rcpt} onOpenChange={() => setRcpt(null)} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
